import { basename } from "node:path";
import {
  daemonRuntimeBundlesPath,
  daemonServiceRecognizedExecutableWrapperPaths,
} from "../../config/daemon-state";
import type { WakeLoopPathResolutionInput } from "../../config/paths";

const COMMAND_TOKEN_PATTERN = /"([^"]*)"|'([^']*)'|(\S+)/g;

export function tokenizeDaemonCommand(command: string): string[] {
  const tokens: string[] = [];
  for (const match of command.matchAll(COMMAND_TOKEN_PATTERN)) {
    const token = match[1] ?? match[2] ?? match[3];
    if (token) {
      tokens.push(token);
    }
  }
  return tokens;
}

export function normalizeDaemonCommandPath(path: string): string {
  return path.trim().replaceAll("\\", "/").toLowerCase();
}

export function extractDaemonCommandEntryPath(command: string): string | null {
  const unquotedWrapperPath = extractUnquotedDaemonWrapperPath(command);
  if (unquotedWrapperPath) {
    return unquotedWrapperPath;
  }

  const tokens = tokenizeDaemonCommand(command);
  if (tokens.length === 0) {
    return null;
  }
  const serviceRunIndex = tokens.findIndex(
    (token, index) => token === "service" && tokens[index + 1] === "run"
  );
  const candidateTokens =
    serviceRunIndex > 0 ? tokens.slice(0, serviceRunIndex) : tokens;

  for (let index = candidateTokens.length - 1; index >= 0; index -= 1) {
    const token = candidateTokens[index];
    if (!(token && looksLikeDaemonEntryPath(token))) {
      continue;
    }
    return token;
  }

  return null;
}

export function resolveDaemonBundleIdFromEntryPath(
  entryPath: string,
  pathInput: WakeLoopPathResolutionInput = {}
): string | null {
  const normalizedEntryPath = normalizeDaemonCommandPath(entryPath);
  const normalizedBundleRoot = `${normalizeDaemonCommandPath(
    daemonRuntimeBundlesPath(pathInput)
  )}/`;
  if (!normalizedEntryPath.startsWith(normalizedBundleRoot)) {
    return null;
  }
  const relative = normalizedEntryPath.slice(normalizedBundleRoot.length);
  const bundleId = relative.split("/")[0];
  return bundleId && bundleId.length > 0 ? bundleId : null;
}

function looksLikeDaemonEntryPath(token: string): boolean {
  return (
    token.includes("/bundles/") ||
    token.includes("wakeloop/dist/wakeloop.js") ||
    token.endsWith("/wakeloop.js") ||
    token.endsWith("/wakeloop-dev.js") ||
    token.endsWith("/daemon-service-launcher.mjs") ||
    isRecognizedDaemonServiceExecutableWrapperPath(token)
  );
}

function isRecognizedDaemonServiceExecutableWrapperPath(
  token: string
): boolean {
  const normalizedToken = normalizeDaemonCommandPath(token);
  return daemonServiceRecognizedExecutableWrapperPaths().some((wrapperPath) => {
    const normalizedWrapperPath = normalizeDaemonCommandPath(wrapperPath);
    return (
      normalizedToken === normalizedWrapperPath ||
      normalizedToken.endsWith(
        `/${normalizeDaemonCommandPath(basename(wrapperPath))}`
      )
    );
  });
}

function extractUnquotedDaemonWrapperPath(command: string): string | null {
  const normalizedCommand = normalizeDaemonCommandPath(command);
  const serviceRunIndex = normalizedCommand.indexOf(" service run");
  if (serviceRunIndex <= 0) {
    return null;
  }

  const commandPrefix = command.slice(0, serviceRunIndex);
  const normalizedPrefix = normalizedCommand.slice(0, serviceRunIndex);
  for (const wrapperPath of daemonServiceRecognizedExecutableWrapperPaths()) {
    const wrapperName = basename(wrapperPath);
    if (!wrapperName.includes(" ")) {
      continue;
    }
    const normalizedSuffix = `/${normalizeDaemonCommandPath(wrapperName)}`;
    const wrapperNameIndex = normalizedPrefix.lastIndexOf(normalizedSuffix);
    if (wrapperNameIndex < 0) {
      continue;
    }
    return commandPrefix.slice(0, wrapperNameIndex + normalizedSuffix.length);
  }
  return null;
}
